/**
 * Campaign-scoped API modules: stats, sms, messages, delivery, exports.
 * Depends on apiFetch, getCampaignId, authToken from client.js
 */
import { apiFetch, getCampaignId, getToken } from './client.js';

const API_BASE = '/api';

// Stats
export const stats = {
  get: () => apiFetch(`/c/${getCampaignId()}/stats`),
  daily: (date) => apiFetch(`/c/${getCampaignId()}/stats/daily?date=${date}`),
};

// SMS
export const sms = {
  send: (data) => apiFetch(`/c/${getCampaignId()}/sms/send`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  beforeDelivery: (stopId, estimatedTime) => apiFetch(`/c/${getCampaignId()}/sms/before-delivery/${stopId}`, {
    method: 'POST', body: JSON.stringify({ estimatedTime }),
  }),
  afterDelivery: (stopId) => apiFetch(`/c/${getCampaignId()}/sms/after-delivery/${stopId}`, {
    method: 'POST',
  }),
  log: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/c/${getCampaignId()}/sms/log${qs ? '?' + qs : ''}`);
  },
};

// Messages (chat)
export const messages = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/c/${getCampaignId()}/messages${qs ? '?' + qs : ''}`);
  },
  send: (text, planId = null, attachmentUrl = null) => apiFetch(`/c/${getCampaignId()}/messages`, {
    method: 'POST', body: JSON.stringify({ text, planId, attachmentUrl }),
  }),
  markRead: (messageId) => apiFetch(`/c/${getCampaignId()}/messages/${messageId}/read`, {
    method: 'PUT',
  }),
};

// Delivery (scan, signature, upload, appointments)
export const delivery = {
  scan: (image, mediaType) => apiFetch(`/c/${getCampaignId()}/scan`, {
    method: 'POST', body: JSON.stringify({ image, mediaType }),
  }),
  validateScan: (serialNumber) => apiFetch(`/c/${getCampaignId()}/scan/validate`, {
    method: 'POST', body: JSON.stringify({ serialNumber }),
  }),
  recordScan: (itemId, data) => apiFetch(`/c/${getCampaignId()}/scan/record`, {
    method: 'POST', body: JSON.stringify({ itemId, ...data }),
  }),
  scanHistory: (date) => {
    const d = date || new Date().toISOString().slice(0, 10);
    return apiFetch(`/c/${getCampaignId()}/scan/history?date=${d}`);
  },
  saveSignature: (data) => apiFetch(`/c/${getCampaignId()}/signatures`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  uploadPhoto: (file, type, gpsLat, gpsLng) => {
    const form = new FormData();
    form.append('photo', file);
    form.append('type', type || 'photo');
    if (gpsLat != null) form.append('gpsLat', String(gpsLat));
    if (gpsLng != null) form.append('gpsLng', String(gpsLng));
    return apiFetch(`/c/${getCampaignId()}/upload`, { method: 'POST', body: form });
  },
  createAppointment: (data) => apiFetch(`/c/${getCampaignId()}/appointments`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  retryFnuci: (itemId) => apiFetch(`/c/${getCampaignId()}/items/${itemId}/fnuci/retry`, {
    method: 'POST', body: JSON.stringify({}),
  }),
};

// DS3 mappings (box number ↔ FNUCI cross-validation)
export const ds3 = {
  getCount: () => apiFetch(`/c/${getCampaignId()}/ds3/count`),
  importFile: (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch(`/c/${getCampaignId()}/ds3/import`, { method: 'POST', body: form });
  },
  validate: (fnuci, boxNumber) => apiFetch(`/c/${getCampaignId()}/ds3/validate`, {
    method: 'POST', body: JSON.stringify({ fnuci, boxNumber }),
  }),
};

/**
 * Download a file export (triggers browser download).
 * Supports CSV, XLSX, and PDF exports with optional query filters.
 * @param {string} path - Relative export path (e.g., 'clients.csv', 'clients.xlsx')
 * @param {string} filename - Download filename
 * @param {object} [filters] - Optional query params
 */
export function downloadExport(path, filename, filters = {}) {
  const cid = getCampaignId();
  const qs = new URLSearchParams(filters).toString();
  const url = `${API_BASE}/c/${cid}/export/${path}${qs ? '?' + qs : ''}`;
  return fetch(url, {
    headers: {
      'Authorization': `Bearer ${getToken()}`,
      'X-Campaign-Id': cid,
    },
  })
    .then((r) => {
      if (!r.ok) throw new Error(`Erreur export (${r.status})`);
      return r.blob();
    })
    .then((blob) => {
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename || path;
      a.click();
      URL.revokeObjectURL(objUrl);
    })
    .catch((err) => {
      alert(`Échec de l'export : ${err.message}`);
    });
}
